import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { ChevronDown } from "lucide-react";
import { Children, useState, type FC, type ReactNode } from "react";

interface ThreadGroupProps {
    title: string;
    hideWhenEmpty?: boolean;
    /** Rendered at the heading row's right edge, outside the collapse trigger, so activating it never toggles the group. */
    action?: ReactNode;
    children: ReactNode;
}

// Composite for a labeled sidebar group (Pinned / Shared with me / Recents). Design
// tokens — typography, chevron, hover, spacing — live in `.impeccable.md`
// → "Thread list groups". Collapse state is intentionally in-memory only.
export const ThreadGroup: FC<ThreadGroupProps> = ({
    title,
    hideWhenEmpty = false,
    action,
    children,
}) => {
    const [open, setOpen] = useState(true);

    if (hideWhenEmpty && Children.toArray(children).length === 0) return null;

    return (
        <Collapsible open={open} onOpenChange={setOpen}>
            <div className="relative">
                <CollapsibleTrigger
                    className={cn(
                        "flex h-9 w-full items-center gap-2 rounded-lg pl-3",
                        action ? "pr-10" : "pr-3",
                        "text-foreground text-sm font-medium",
                        "hover:bg-muted focus-visible:bg-muted focus-visible:outline-none",
                        "transition-colors motion-reduce:transition-none"
                    )}
                >
                    <ChevronDown
                        aria-hidden="true"
                        data-state={open ? "open" : "closed"}
                        className="text-muted-foreground size-4 transition-transform duration-150 ease-out data-[state=closed]:-rotate-90 motion-reduce:transition-none"
                    />
                    <span
                        role="heading"
                        aria-level={3}
                        className="min-w-0 flex-1 truncate text-start"
                    >
                        {title}
                    </span>
                </CollapsibleTrigger>
                {action ? (
                    <div className="absolute top-2 right-2.5 flex">
                        {action}
                    </div>
                ) : null}
            </div>
            <CollapsibleContent>
                <div className="flex flex-col gap-0.5">{children}</div>
            </CollapsibleContent>
        </Collapsible>
    );
};
